import { Flex } from '@repo/ui/Flex';
import { Box } from '@repo/ui/Box';
import { IconButton } from '@repo/ui/IconButton';
import { Popover } from '@repo/ui/Popover';
import { PopoverContentSimple } from '@repo/ui/Popover/PopoverContentSimple';
import { PopoverRoot } from '@repo/ui/Popover/PopoverRoot';
import { PopoverTrigger } from '@repo/ui/Popover/PopoverTrigger';

import type { OrderItem } from '../../../graphql/query/getOrder';
import { rawString, Text } from '../../Language/Language';
import {
  eligibleHeader,
  itemSelectionStepId,
} from '../../../layouts/ItemSelection/baseConfig';
import { ItemCardButtonView } from '../../ItemCard/ItemCardView';
import { ShipmentGroup } from '../../ShipmentGroup/ShipmentGroup';
import type { ShipmentGroup as ShipmentGroupType } from '../../../utils/item';
import s from './ItemSelectionTask.module.css';
import { ShowMoreButton, showMoreCutoff } from './ShowMoreButton';

export interface EligibleItemsViewProps {
  eligibleItems: OrderItem[];
  hasMultipleShipments: boolean;
  shipmentGroups: ShipmentGroupType[];
  isMobile: boolean;
  showMore: boolean;
  onShowMore: () => void;
  onItemSelect: (itemId: string, trackingNumber?: string) => void;
  // Optional - for quantity selection if needed
  remainingQuantity?: number;
}

/**
 * Pure presentational component for displaying eligible items.
 * Contains no business logic, hooks, or side effects.
 * All data and callbacks are provided via props.
 */
export const EligibleItemsView = ({
  eligibleItems,
  hasMultipleShipments,
  shipmentGroups,
  isMobile,
  showMore,
  onShowMore,
  onItemSelect,
  remainingQuantity,
}: EligibleItemsViewProps) => {
  if (!eligibleItems || eligibleItems.length === 0) {
    return null;
  }

  return (
    <Box className={s.wrapper} padding={['px-24', 'md:px-0']}>
      <Flex alignItems="center" className={s.headerContainer} gap="g-8">
        <h1 className={s[`${itemSelectionStepId}-${eligibleHeader}`]}>
          <Text langKey={`Global.${itemSelectionStepId}.EligibleHeader`} />
        </h1>
        <PopoverRoot>
          <PopoverTrigger asChild={true}>
            <IconButton
              icon="help-circle"
              label={rawString({
                langKey: 'Global.help',
              })}
            />
          </PopoverTrigger>
          <Popover
            align={isMobile ? 'end' : 'center'}
            ariaLabel={rawString({
              langKey: 'Global.info',
            })}
            hasArrow={false}
          >
            <PopoverContentSimple
              closeButtonLabel={rawString({
                langKey: 'Global.close',
              })}
            >
              <Text langKey="Global.itemSelection.EligibleHeaderHelp" />
            </PopoverContentSimple>
          </Popover>
        </PopoverRoot>
      </Flex>

      {hasMultipleShipments ? (
        <Flex direction="column" gap="g-24">
          {shipmentGroups.map((shipmentGroup) => {
            // Filter eligible items for this shipment
            const eligibleItemsInShipment = shipmentGroup.items.filter((item) =>
              eligibleItems.some((eligibleItem) => eligibleItem.id === item.id),
            );

            if (eligibleItemsInShipment.length === 0) return null;

            return (
              <ShipmentGroup
                key={shipmentGroup.shipmentIndex}
                shipmentGroup={{
                  ...shipmentGroup,
                  items: eligibleItemsInShipment,
                }}
                onItemSelect={(itemId, trackingNumber) =>
                  onItemSelect(itemId, trackingNumber)
                }
                imageSize={isMobile ? 'sm' : 'xl'}
                showShipmentDetails={true}
                showMoreCutoff={showMoreCutoff}
              />
            );
          })}
        </Flex>
      ) : (
        <Flex direction="column" gap="g-16">
          {eligibleItems.map((item, i) => {
            if (!showMore && i > showMoreCutoff - 1) return null;

            return (
              <ItemCardButtonView
                key={item.id}
                onClick={() => onItemSelect(item.id)}
                aria-labelledby={`${item.id}-title`}
                imageSize={isMobile ? 'sm' : 'xl'}
                item={item}
                remainingQuantity={
                  remainingQuantity || item.quantity?.remaining || 1
                }
                isShipmentScopeReason={false}
              />
            );
          })}
        </Flex>
      )}

      {!hasMultipleShipments && (
        <ShowMoreButton
          onClick={onShowMore}
          showMore={showMore}
          hiddenItemCount={eligibleItems.length - showMoreCutoff}
        />
      )}
    </Box>
  );
};
